import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { Step1SelectBatch } from './Step1SelectBatch';
import { Step2Configure } from './Step2Configure';
import { Step3Review } from './Step3Review';
import { WIZARD_STEPS } from './types';
import { useReallocationWizard } from './useReallocationWizard';
import type { ReallocationOperation } from '../../context/editProjectReducer';

interface ReallocationWizardProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onConfirm: (operations: ReallocationOperation[]) => void;
}

export const ReallocationWizard = ({
  open,
  projectId,
  onClose,
  onConfirm,
}: ReallocationWizardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    isLoading,
    batches,
    activeStep,
    selectedBatches,
    selectedBatchDetails,
    rowsByBatch,
    canProceedFromSelect,
    configureValid,
    operations,
    toggleBatch,
    goNext,
    goBack,
    addRow,
    updateRow,
    removeRow,
  } = useReallocationWizard({ open, projectId });

  const dialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === WIZARD_STEPS.length - 1;
  const nextDisabled =
    (isFirstStep && !canProceedFromSelect) || (activeStep === 1 && !configureValid);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          width: '1000px',
          maxWidth: '95vw',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          {formatMessage({ id: 'adminProjects.reallocation.modal.title' })}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers ref={dialogContentRef}>
        <Stepper activeStep={activeStep} sx={{ mb: theme.spacing(4) }}>
          {WIZARD_STEPS.map((step) => (
            <Step key={step.key}>
              <StepLabel>{formatMessage({ id: step.labelId })}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Step1SelectBatch
            batches={batches}
            selectedBatches={selectedBatches}
            onToggle={toggleBatch}
            isLoading={isLoading}
          />
        )}
        {activeStep === 1 && (
          <Step2Configure
            projectId={projectId}
            selectedBatchDetails={selectedBatchDetails}
            rowsByBatch={rowsByBatch}
            onAddRow={addRow}
            onUpdateRow={updateRow}
            onRemoveRow={removeRow}
          />
        )}
        {activeStep === 2 && (
          <Step3Review selectedBatchDetails={selectedBatchDetails} rowsByBatch={rowsByBatch} />
        )}
      </DialogContent>

      <DialogActions sx={{ p: theme.spacing(3) }}>
        {isFirstStep ? (
          <Button
            id="reallocation-cancel-button"
            variant="outlined"
            onClickHandler={onClose}
            displayLabel={formatMessage({ id: 'adminProjects.reallocation.cancel' })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
          />
        ) : (
          <Button
            id="reallocation-back-button"
            variant="outlined"
            onClickHandler={goBack}
            displayLabel={formatMessage({ id: 'adminProjects.reallocation.back' })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
          />
        )}
        {isLastStep ? (
          <Button
            id="reallocation-confirm-button"
            variant="contained"
            startIcon={<CheckIcon />}
            onClickHandler={() => onConfirm(operations)}
            displayLabel={formatMessage({ id: 'adminProjects.reallocation.confirm' })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              backgroundColor: theme.palette.primary.main,
            }}
          />
        ) : (
          <Button
            id="reallocation-next-button"
            variant="contained"
            onClickHandler={goNext}
            disabled={nextDisabled}
            displayLabel={formatMessage({ id: 'adminProjects.reallocation.next' })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              backgroundColor: theme.palette.primary.main,
            }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};
